import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookUser, X, FileDown, UserPlus, Trash2 } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { downloadPdf } from "./RgpdExportButton";

const fr = (d) => (d ? String(d).split("-").reverse().join("/") : "—");
const key = (l) => `${l.agent_id}|${l.start}`;
const Row = ({ l, children, testid }) => (
  <tr data-testid={testid} className="hover:bg-slate-50">
    {children}
    <td className="px-2 py-1.5 font-semibold">{l.last_name}</td><td className="px-2">{l.first_name}</td><td className="px-2">{l.nationality || "—"}</td>
    <td className="px-2">{fr(l.birth_date)}</td><td className="px-2">{l.sex || "—"}</td><td className="px-2">{l.job || "—"}</td><td className="px-2">{l.qualification || "—"}</td><td className="px-2">{l.contract || "—"}</td>
    <td className="px-2">{fr(l.entry)}</td><td className="px-2">{fr(l.exit)}</td><td className="px-2">{l.permit || "—"}</td>
  </tr>
);
const HEAD = ["Nom", "Prénoms", "Nationalité", "Naissance", "Sexe", "Emploi", "Qualification", "Contrat", "Entrée", "Sortie", "Titre de travail"];

export const PersonnelRegistryDialog = ({ onClose }) => {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get("/personnel-registry").then((r) => { setD(r.data); setSel(new Set(r.data.pending.map(key))); }).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const toggle = (k) => setSel((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const add = async () => {
    setBusy(true);
    try {
      const items = d.pending.filter((l) => sel.has(key(l))).map((l) => ({ agent_id: l.agent_id, start: l.start }));
      const r = await http.post("/personnel-registry", { items });
      toast.success(`${r.data.added} inscription(s) ajoutée(s) au registre`); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Retirer la ligne n° ${l.order} (${l.last_name} ${l.first_name}) du registre ?`)) return;
    try { await http.delete(`/personnel-registry/${l.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const pdf = () => downloadPdf("/personnel-registry.pdf", "Registre_du_personnel.pdf").catch((e) => toast.error(formatApiError(e)));
  const allSel = d && d.pending.length > 0 && d.pending.every((l) => sel.has(key(l)));
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div data-testid="registry-dialog" className="bg-white rounded-md shadow-xl w-full max-w-7xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b">
          <BookUser className="h-5 w-5" /><h3 className="font-display text-lg font-extrabold">Registre unique du personnel</h3>
          <button type="button" data-testid="registry-pdf" onClick={pdf} className="ml-auto inline-flex items-center gap-1.5 bg-slate-900 text-white px-3 py-2 rounded-md text-sm font-semibold"><FileDown className="h-4 w-4" />Générer le PDF</button>
          <button type="button" data-testid="registry-close" onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        {!d ? <p className="p-6 text-sm text-slate-500">Chargement…</p> : (
          <div className="overflow-auto p-5 space-y-6 text-xs">
            <section>
              <h4 className="font-bold text-sm mb-2">Inscrits au registre ({d.lines.length})</h4>
              <table className="w-full border border-slate-200 whitespace-nowrap" data-testid="registry-lines">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 text-left"><tr><th className="px-2 py-1.5">N°</th>{HEAD.map((h) => <th key={h} className="px-2">{h}</th>)}{d.can_edit && <th />}</tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {d.lines.length === 0 && <tr><td colSpan={13} className="text-center text-slate-500 py-6">Registre vide.</td></tr>}
                  {d.lines.map((l) => (
                    <tr key={l.id} data-testid={`registry-line-${l.order}`} className="hover:bg-slate-50">
                      <td className="px-2 py-1.5 font-mono">{l.order}</td><td className="px-2 font-semibold">{l.last_name}</td><td className="px-2">{l.first_name}</td><td className="px-2">{l.nationality || "—"}</td>
                      <td className="px-2">{fr(l.birth_date)}</td><td className="px-2">{l.sex || "—"}</td><td className="px-2">{l.job || "—"}</td><td className="px-2">{l.qualification || "—"}</td><td className="px-2">{l.contract || "—"}</td>
                      <td className="px-2">{fr(l.entry)}</td><td className="px-2">{fr(l.exit)}</td><td className="px-2">{l.permit || "—"}</td>
                      {d.can_edit && <td className="px-2"><button type="button" data-testid={`registry-remove-${l.order}`} onClick={() => remove(l)} title="Retirer" className="p-1 rounded hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5 text-rose-600" /></button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            {d.can_edit ? (
              <section>
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-bold text-sm">Contrats à inscrire ({d.pending.length})</h4>
                  <span className="text-slate-500">Agents actifs et sortis depuis moins de 5 ans · une ligne par contrat</span>
                  <button type="button" data-testid="registry-add" disabled={!sel.size || busy} onClick={add} className="ml-auto inline-flex items-center gap-1.5 bg-emerald-700 text-white px-3 py-1.5 rounded-md text-sm font-semibold disabled:opacity-50"><UserPlus className="h-4 w-4" />Ajouter au registre ({sel.size})</button>
                </div>
                <table className="w-full border border-slate-200 whitespace-nowrap" data-testid="registry-pending">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 text-left"><tr><th className="px-2 py-1.5"><input type="checkbox" data-testid="registry-select-all" checked={!!allSel} onChange={() => setSel(allSel ? new Set() : new Set(d.pending.map(key)))} /></th>{HEAD.map((h) => <th key={h} className="px-2">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {d.pending.length === 0 && <tr><td colSpan={12} className="text-center text-slate-500 py-6">Tous les contrats sont inscrits.</td></tr>}
                    {d.pending.map((l) => (
                      <Row key={key(l)} l={l} testid={`registry-pending-${l.agent_id}-${l.start}`}>
                        <td className="px-2 py-1.5"><input type="checkbox" data-testid={`registry-check-${l.agent_id}-${l.start}`} checked={sel.has(key(l))} onChange={() => toggle(key(l))} /></td>
                      </Row>
                    ))}
                  </tbody>
                </table>
              </section>
            ) : <p className="text-slate-500">Seul un administrateur peut inscrire de nouveaux salariés au registre.</p>}
          </div>
        )}
      </div>
    </div>
  );
};
